import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { SignatorySelect } from "./SignatoriesCard";
import { Download, Loader2, Printer, Save, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Letterhead, withSigner } from "./Letterhead";
import { DocEditor } from "./DocEditor";
import { printDecision, decisionPDFBlob } from "../lib/decisionPrint";
import { LetterTemplates } from "./LetterTemplates";
import { personMatch } from "../lib/searchText";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const today = () => new Date().toLocaleDateString("en-CA");

const InsertRider = ({ employees, id, setId, onInsert }) => {
  const [q, setQ] = useState("");
  const [withId, setWithId] = useState(true);
  const list = useMemo(() => {
    const s = q.trim();
    return (employees || []).filter((e) => personMatch(e, s));
  }, [employees, q]);
  const add = () => {
    const e = (employees || []).find((x) => x.id === id);
    if (!e) { toast.error("اختر المندوب"); return; }
    onInsert(`<b>${esc(e.name)}</b>${withId && e.iqama_number ? `، رقم الهوية/الإقامة: ${esc(e.iqama_number)}` : ""}&nbsp;`);
  };
  return (
    <div className="space-y-1.5 rounded-xl border border-indigo-100 bg-indigo-50/60 p-2.5" data-testid="letter-insert-rider">
      <p className="flex items-center gap-1.5 text-xs font-bold text-indigo-800"><UserPlus className="h-3.5 w-3.5" /> الموظف / المندوب (للتعبئة والإدراج)</p>
      <Input data-testid="letter-rider-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو الإقامة أو الجوال…" className="h-9 bg-white" />
      <select data-testid="letter-rider-select" value={id} onChange={(e) => setId(e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
        <option value="">— اختر المندوب —</option>
        {list.map((e) => <option key={e.id} value={e.id}>{e.name}{e.branch ? ` · ${e.branch}` : ""}</option>)}
      </select>
      <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={withId} onChange={(e) => setWithId(e.target.checked)} data-testid="letter-rider-with-id" /> مع رقم الإقامة</label>
      <Button type="button" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={add} className="w-full gap-1.5 bg-indigo-600 hover:bg-indigo-700" data-testid="letter-rider-insert">
        <UserPlus className="h-4 w-4" /> إدراج في مكان المؤشر</Button>
    </div>
  );
};

export const LetterEditor = ({ letter, employees, onOpenChange, onSaved }) => {
  const { company } = useAuth();
  const [signer, setSigner] = useState({});
  const sc = withSigner(company, signer);
  const ed = useRef(null);
  const [f, setF] = useState({ title: letter?.title || "", date: letter?.date || today(), show_stamp: letter?.show_stamp ?? true, recipient_name: letter?.recipient_name || "", recipient_email: letter?.recipient_email || "", signatory_id: letter?.signatory_id || "" });
  const [busy, setBusy] = useState("");
  const [empId, setEmpId] = useState("");
  const emp = (employees || []).find((e) => e.id === empId);
  const data = () => ({ ...letter, ...f, kind: "letter", body_html: ed.current?.getHTML() || "" });
  const pdfName = `${(f.title || "مستند").slice(0, 50)}.pdf`;
  const save = async () => {
    setBusy("save");
    try {
      const body = { ...f, body_html: ed.current?.getHTML() || "" };
      const r = letter?.id ? await api.updateLetter(letter.id, body) : await api.createLetter(body);
      toast.success(`تم حفظ المستند ${r.number}`); onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("pdf");
    try { const b = await decisionPDFBlob(data(), sc, fileUrl); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = pdfName; a.click(); }
    catch { toast.error("تعذّر إنشاء PDF"); } finally { setBusy(""); }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[94vh] overflow-y-auto p-4 sm:p-6" dir="rtl" data-testid="letter-editor-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading">{letter?.id ? `مستند ${letter.number}` : "مستند جديد بكليشة الشركة"}</DialogTitle>
          <DialogDescription>اكتب ما تشاء على ورق الشركة الرسمي</DialogDescription></DialogHeader>
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <aside className="space-y-3">
            <div><label className="text-xs font-semibold text-slate-500">عنوان المستند (اختياري)</label>
              <Input data-testid="letter-title" value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} placeholder="مثال: خطاب تعريف" className="h-9" /></div>
            <div><label className="text-xs font-semibold text-slate-500">التاريخ</label>
              <Input data-testid="letter-date" type="date" value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))} className="h-9" /></div>
            <div><label className="text-xs font-semibold text-slate-500">الجهة المستلمة (للإرسال بالبريد — اختياري)</label>
              <Input data-testid="letter-recipient-name" value={f.recipient_name} onChange={(e) => setF((p) => ({ ...p, recipient_name: e.target.value }))} placeholder="اسم الجهة" className="h-9 mb-1.5" />
              <Input data-testid="letter-recipient-email" type="email" dir="ltr" value={f.recipient_email} onChange={(e) => setF((p) => ({ ...p, recipient_email: e.target.value.trim() }))} placeholder="name@example.com" className="h-9" /></div>
            <InsertRider employees={employees} id={empId} setId={setEmpId} onInsert={(h) => ed.current?.insertHTML(h)} />
            <LetterTemplates ed={ed} emp={emp} ctx={{ company: company?.name || "الشركة", date: f.date, title: f.title }} onTitle={(t) => setF((p) => ({ ...p, title: t }))} />
            <SignatorySelect testid="letter-signatory" value={f.signatory_id} onChange={(v) => setF((p) => ({ ...p, signatory_id: v }))} onPick={setSigner} />
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <span className="text-sm text-slate-700">إظهار الختم والتوقيع</span>
              <Switch data-testid="letter-show-stamp" checked={f.show_stamp} onCheckedChange={(v) => setF((p) => ({ ...p, show_stamp: v }))} />
            </div>
            <Button data-testid="letter-save" disabled={!!busy} onClick={save} className="w-full gap-1.5">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ المستند</Button>
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="letter-download-pdf" variant="outline" disabled={!!busy} onClick={download} className="gap-1.5">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تنزيل PDF</Button>
              <Button data-testid="letter-print" variant="outline" onClick={() => printDecision(data(), company, fileUrl)} className="gap-1.5"><Printer className="h-4 w-4" /> طباعة</Button>
            </div>
          </aside>
          <DocEditor ref={ed} initialHTML={letter?.body_html || ""}
            header={<>
              <Letterhead company={company} />
              <div className="mb-3 flex justify-between text-xs text-slate-500"><span>الرقم: <b>{letter?.number || "يُولَّد تلقائياً"}</b></span><span>التاريخ: <b>{f.date}</b></span></div>
              {f.title && <h3 className="mb-4 text-center font-heading text-lg font-extrabold underline underline-offset-8">{f.title}</h3>}
            </>}
            footer={f.show_stamp && <div className="mt-10 flex justify-end text-center text-xs text-slate-500"><div className="w-1/2"><b className="text-slate-700">{sc?.official_title || "المدير العام"}</b>
              {sc?.signature_id ? <img src={fileUrl(sc.signature_id)} alt="" className="mx-auto h-32 object-contain" /> : <div className="mx-4 mt-10 border-b border-slate-300" />}
              {company?.stamp_id && <img src={fileUrl(company.stamp_id)} alt="" className="mx-auto h-44 object-contain" />}</div></div>} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
